import { Fragment } from 'react';
import { Box, Flex } from '@chakra-ui/react';

export type AssistantMode = 'prep' | 'live' | 'coach';

const Lock = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="11"
    height="11"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    {...p}
  >
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

export interface PhaseNode {
  mode: AssistantMode;
  label: string;
  locked?: boolean;
  // Tooltip shown when the node is locked, explaining why.
  lockedTip?: string;
}

/**
 * The "Phase" switch — a before → during → after lifecycle rail that flips the Interviewer Assistant
 * between its phases: Prep (the brief you went in with), Live Coach (realtime nudges during/just
 * after the round), and Coach (the retrospective review note). The rail fills across the pins toward
 * the active phase. A node is locked (dim + lock + tooltip) when its surface isn't available yet —
 * e.g. Live before the round starts, or Coach before the transcript is processed.
 *
 * Renders an ordered list of `nodes`; rails sit between them. Two or three phases both work, so the
 * caller can pass only the phases that apply to this round.
 */
export function InterviewAssistantSwitch({
  mode,
  nodes,
  onChange,
}: {
  mode: AssistantMode;
  nodes: PhaseNode[];
  onChange: (_mode: AssistantMode) => void;
}) {
  const activeIndex = nodes.findIndex((n) => n.mode === mode);

  return (
    <Flex className="ia-switch" align="center" userSelect="none">
      {nodes.map((node, i) => {
        const isActive = node.mode === mode;
        return (
          <Fragment key={node.mode}>
            {i > 0 && (
              // A rail is "filled" once the active phase is at or past the node to its right.
              <Box className={`ia-rail${activeIndex >= i ? ' is-filled' : ''}`}>
                <span className="ia-pin" />
              </Box>
            )}
            <Box
              as="button"
              type="button"
              className={`ia-node${isActive ? ' is-active' : ''}${
                node.locked ? ' is-locked' : ''
              }`}
              onClick={() => !node.locked && onChange(node.mode)}
              aria-pressed={isActive}
            >
              <span className="ia-dot" />
              <span className="ia-lab">{node.label}</span>
              {node.locked && (
                <>
                  <span className="ia-lock">
                    <Lock />
                  </span>
                  {node.lockedTip && (
                    <span className="ia-tip">{node.lockedTip}</span>
                  )}
                </>
              )}
            </Box>
          </Fragment>
        );
      })}
    </Flex>
  );
}

export default InterviewAssistantSwitch;
